/* eslint-disable react-refresh/only-export-components -- exportamos
   `classifyUrgency` co-localizado con el componente para que los tests lo usen. */
/**
 * Badge de urgencia según días hasta `closes_at`.
 *
 * Thresholds (Sprint 3):
 * - critical: ≤ 7 días → rojo. "Cierra en X días".
 * - soon:    ≤ 30 días → ámbar. "Cierra en X días".
 * - later:   > 30 días → slate. "Cierra el dd/mm".
 * - none:    closes_at=NULL → slate claro. "Sin fecha de cierre".
 * - quota:   closes_at=NULL y "hasta agotar cupos" (#692) → ámbar. No tiene
 *            fecha por diseño, y los cupos se acaban sin aviso.
 * - expired: closes_at en el pasado → gris oscuro. "Cerrada hace X días".
 *
 * La lógica es pura (recibe `closesAt` como string ISO o null y un `now`
 * opcional para tests deterministas). El estado se expone vía `data-urgency`
 * para que los tests puedan validar transiciones sin parsear texto.
 */

import { parseIsoDate } from "@/lib/dates";

export type UrgencyLevel = "critical" | "soon" | "later" | "none" | "quota" | "expired";

export interface UrgencyInfo {
  level: UrgencyLevel;
  /** Días restantes (negativo si expirada, null si sin fecha). */
  daysUntilClose: number | null;
  label: string;
}

const CRITICAL_THRESHOLD_DAYS = 7;
const SOON_THRESHOLD_DAYS = 30;

export function classifyUrgency(
  closesAt: string | null,
  now: Date = new Date(),
  untilQuotaExhausted = false,
): UrgencyInfo {
  if (!closesAt) {
    // Una fecha de cierre, si la hay, manda sobre la modalidad de cupos.
    if (untilQuotaExhausted) {
      return { level: "quota", daysUntilClose: null, label: "Hasta agotar cupos" };
    }
    return { level: "none", daysUntilClose: null, label: "Sin fecha de cierre" };
  }
  // `closes_at` es `date` (sin hora): parseo local para que el dia calendario
  // no se corra en America/Bogota (#537).
  const closes = parseIsoDate(closesAt);
  if (Number.isNaN(closes.getTime())) {
    return { level: "none", daysUntilClose: null, label: "Sin fecha de cierre" };
  }
  // Diferencia en días completos (redondeo a ceil para no mentir — si falta
  // 23h, es "1 día", no "0 días").
  const ms = closes.getTime() - now.getTime();
  const days = Math.ceil(ms / (1000 * 60 * 60 * 24));

  if (days < 0) {
    return {
      level: "expired",
      daysUntilClose: days,
      label: `Cerrada hace ${Math.abs(days)} ${Math.abs(days) === 1 ? "día" : "días"}`,
    };
  }
  if (days <= CRITICAL_THRESHOLD_DAYS) {
    return {
      level: "critical",
      daysUntilClose: days,
      label: days === 0 ? "Cierra hoy" : `Cierra en ${days} ${days === 1 ? "día" : "días"}`,
    };
  }
  if (days <= SOON_THRESHOLD_DAYS) {
    return {
      level: "soon",
      daysUntilClose: days,
      label: `Cierra en ${days} días`,
    };
  }
  // > 30 días: mostramos fecha en vez de contador (sino "Cierra en 187 días"
  // no aporta).
  return {
    level: "later",
    daysUntilClose: days,
    label: `Cierra ${closes.toLocaleDateString("es-CO", {
      day: "2-digit",
      month: "short",
    })}`,
  };
}

const LEVEL_CLASSES: Record<UrgencyLevel, string> = {
  critical: "bg-red-50 text-red-700 ring-red-200",
  soon: "bg-amber-50 text-amber-800 ring-amber-200",
  later: "bg-slate-100 text-slate-700 ring-slate-200",
  none: "bg-slate-50 text-slate-500 ring-slate-200",
  quota: "bg-amber-50 text-amber-800 ring-amber-200",
  expired: "bg-slate-200 text-slate-700 ring-slate-300",
};

export interface UrgencyBadgeProps {
  closesAt: string | null;
  /** Recibe postulaciones "hasta agotar cupos" (#692). */
  untilQuotaExhausted?: boolean;
  /** Override para tests deterministas. */
  now?: Date;
  className?: string;
}

export function UrgencyBadge({ closesAt, untilQuotaExhausted, now, className }: UrgencyBadgeProps) {
  const info = classifyUrgency(closesAt, now, untilQuotaExhausted);
  const base =
    "inline-flex items-center rounded-full px-2.5 py-0.5 text-[11px] font-semibold ring-1 ring-inset";
  return (
    <span
      data-testid="urgency-badge"
      data-urgency={info.level}
      className={`${base} ${LEVEL_CLASSES[info.level]} ${className ?? ""}`}
    >
      {info.label}
    </span>
  );
}
